import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { gradeBlurbsApi } from "../../../service/gradeBlurbs"

type Row = { gradeLetter: string; blurb: string }

// Edit the cheery one-liner that appears on the report card next to the
// student's name (the Congrats layer). One row per grade letter — the
// trailing "Congratulations, <Name>!" / "Stay strong, <Name>." is appended
// automatically by the renderer; this page only edits the blurb itself.
const GradeBlurbs = () => {
  const [items, setItems] = useState<Row[]>([])
  const [serverSnapshot, setServerSnapshot] = useState("")
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [newLetter, setNewLetter] = useState("")
  const [newBlurb, setNewBlurb] = useState("")
  const [removeConfirm, setRemoveConfirm] = useState<string | null>(null)

  const load = () => {
    setLoading(true)
    gradeBlurbsApi.list()
      .then(r => {
        const rows = (r.data || []).map(b => ({ gradeLetter: b.gradeLetter, blurb: b.blurb || "" }))
        setItems(rows)
        setServerSnapshot(JSON.stringify(rows))
      })
      .catch(() => toast.error("Failed to load grade blurbs"))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const setRow = (letter: string, blurb: string) =>
    setItems(cur => cur.map(r => (r.gradeLetter === letter ? { ...r, blurb } : r)))

  const addRow = () => {
    const letter = newLetter.trim().toUpperCase()
    const blurb = newBlurb.trim()
    if (!letter) { toast.error("Enter a grade letter (e.g. O, S, NI)."); return }
    if (!blurb) { toast.error("Enter a blurb to go with the letter."); return }
    if (items.some(r => r.gradeLetter === letter)) { toast.error(`A row for "${letter}" already exists — edit it inline.`); return }
    setItems(cur => [...cur, { gradeLetter: letter, blurb }])
    setNewLetter("")
    setNewBlurb("")
  }

  const doRemove = async (letter: string) => {
    try {
      await gradeBlurbsApi.remove(letter)
      toast.success(`Removed "${letter}"`)
      load()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Remove failed")
    }
  }

  const save = async () => {
    setSaving(true)
    try {
      const res = await gradeBlurbsApi.bulkSave(items)
      const { updated = 0, inserted = 0 } = res.data || {}
      toast.success(inserted ? `Saved ${updated} blurb(s) (${inserted} new letter(s) added).` : `Saved ${updated} blurb(s)`)
      load()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const dirty = JSON.stringify(items) !== serverSnapshot

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex align-items-start justify-content-between mb-3">
          <div>
            <h4 className="mb-1">Grade Blurbs</h4>
            <p className="text-muted small mb-0">
              Edit the cheery one-liner that appears on the report card next to the student's name, one per grade letter.
              The Congrats layer in the report card designer picks the line matching the student's overall grade.
            </p>
          </div>
          <button className="btn btn-dark btn-sm" onClick={save} disabled={saving || !dirty}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>

        <div className="card">
          <div className="card-body p-0">
            <table className="table table-bordered mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 100 }}>Grade</th>
                  <th>Blurb</th>
                  <th style={{ width: 90 }} className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} className="text-center text-muted py-4">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={3} className="text-center text-muted py-4">No grade blurbs yet.</td></tr>
                ) : (
                  items.map(r => (
                    <tr key={r.gradeLetter}>
                      <td className="fw-bold font-monospace">{r.gradeLetter}</td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={r.blurb}
                          placeholder="e.g. Outstanding Effort!"
                          onChange={e => setRow(r.gradeLetter, e.target.value)}
                        />
                      </td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-link text-danger" onClick={() => setRemoveConfirm(r.gradeLetter)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
                <tr className="table-light">
                  <td>
                    <input
                      type="text"
                      maxLength={4}
                      className="form-control form-control-sm font-monospace"
                      placeholder="O"
                      value={newLetter}
                      onChange={e => setNewLetter(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") addRow() }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="e.g. Outstanding!"
                      value={newBlurb}
                      onChange={e => setNewBlurb(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") addRow() }}
                    />
                  </td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-secondary" onClick={addRow}>+ Add</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-muted small mt-3">
          The trailing <strong>"Congratulations, &lt;Name&gt;!"</strong> (and <strong>"Stay strong, &lt;Name&gt;."</strong> for
          fail grades) is appended automatically by the renderer — you only edit the blurb itself here. Letters not listed
          here fall back to a built-in default.
        </p>
      </div>

      {removeConfirm && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title">Remove grade blurb</h5>
                <button type="button" className="btn-close" onClick={() => setRemoveConfirm(null)} />
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  Remove the blurb for "{removeConfirm}"? The report card will fall back to a built-in default.
                </p>
              </div>
              <div className="modal-footer border-0 d-flex gap-2 justify-content-end">
                <button className="btn btn-secondary" onClick={() => setRemoveConfirm(null)}>Cancel</button>
                <button
                  className="btn btn-danger"
                  onClick={() => { const l = removeConfirm; setRemoveConfirm(null); if (l) doRemove(l) }}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GradeBlurbs
